/** All MapLibre sources/layers/filters for the venue map. One source per data
 *  type, floor switching via setFilter — features without floor_id (outdoor
 *  decorations, water, trees) are always visible. */
import { applyBasemap3D } from "../../lib/basemap3d.js";

const UNIT_DEFAULT = "#e9e2d4";
const HIGHLIGHT = "#ff5533";

export const floorFilter = (floorId) => [
  "any",
  ["!", ["has", "floor_id"]],
  ["==", ["get", "floor_id"], floorId],
];

const withKind = (kinds, floorId) => [
  "all",
  ["in", ["get", "kind"], ["literal", kinds]],
  floorFilter(floorId),
];

/** POI markers are NOT here since item 0371: they are an HTML overlay lifted
 *  onto the roofs (poiMarkers.js), because a symbol layer can only draw at
 *  ground level and every pitched view put markers on the neighbour's roof. */
export function addVenueLayers(map, featuresFC, floorId, { basemap3d = false } = {}) {
  // Flatten the basemap: its own OSM 3D buildings (gray extrusions) would
  // clutter and z-fight the venue. Footprint fill layers stay, so the
  // surroundings read as a flat city plan — the Situm look.
  //
  // That is now the DEFAULT rather than the law: a venue whose surroundings are
  // the point (a marina, an open-air centre) can turn them on with
  // settings.basemap_3d (item 0107). The viewer never asks for flat context —
  // adding footprints the basemap does not draw at venue zoom would change how
  // every existing venue looks, and off has to mean exactly what it means today.
  applyBasemap3D(map, basemap3d);

  map.addSource("features", { type: "geojson", data: featuresFC });

  // Ground carpet: slabs and zones, barely extruded.
  map.addLayer({
    id: "slabs",
    type: "fill-extrusion",
    source: "features",
    filter: withKind(["slab", "zone"], floorId),
    paint: {
      "fill-extrusion-color": ["coalesce", ["get", "color"], "#d8d4cc"],
      "fill-extrusion-height": ["get", "height"],
      "fill-extrusion-base": ["get", "base"],
    },
  });

  // Decorative 3D assets: trees, boats, cars, water.
  map.addLayer({
    id: "decorations",
    type: "fill-extrusion",
    source: "features",
    filter: withKind(["decoration"], floorId),
    paint: {
      "fill-extrusion-color": ["coalesce", ["get", "color"], "#9aa2ab"],
      "fill-extrusion-height": ["get", "height"],
      "fill-extrusion-base": ["get", "base"],
    },
  });

  // Buildings/shop units, colored by category (via API), highlight on select.
  map.addLayer({
    id: "units",
    type: "fill-extrusion",
    source: "features",
    filter: withKind(["unit", "shell"], floorId),
    paint: {
      "fill-extrusion-color": [
        "case",
        ["boolean", ["feature-state", "selected"], false],
        HIGHLIGHT,
        ["coalesce", ["get", "color"], UNIT_DEFAULT],
      ],
      "fill-extrusion-height": ["get", "height"],
      "fill-extrusion-base": ["get", "base"],
      "fill-extrusion-opacity": 0.95,
    },
  });
}

const FLOOR_LAYERS = ["slabs", "decorations", "units"];

export function setFloor(map, floorId) {
  map.setFilter("slabs", withKind(["slab", "zone"], floorId));
  map.setFilter("decorations", withKind(["decoration"], floorId));
  map.setFilter("units", withKind(["unit", "shell"], floorId));
  const routeLayers = ["route-casing", "route-line"];
  for (const id of routeLayers) {
    if (map.getLayer(id)) {
      map.setFilter(id, ["==", ["get", "floor_id"], floorId]);
    }
  }
  if (map.getLayer("route-transitions")) {
    map.setFilter("route-transitions", ["==", ["get", "floor_id"], floorId]);
  }
}

export function setSelectedFeature(map, featureId, previousId) {
  if (previousId != null) {
    map.setFeatureState({ source: "features", id: previousId }, { selected: false });
  }
  if (featureId != null) {
    map.setFeatureState({ source: "features", id: featureId }, { selected: true });
  }
}

export { FLOOR_LAYERS };
